import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import WalledContent from 'components/Common/WalledContent'
import OfferPageStickyFooter from 'components/OfferPage/OfferPageStickyFooter'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import Group from 'components/utils/Group'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import {
  arePropertyRatesLoading,
  getPropertyCheapestRates,
  getPropertyFromDurationLabel,
  getPropertyRatesKey,
} from 'selectors/offerPage/offerPageSelectors'

interface Props {
  offer: App.HotelOffer
  onViewPackageOptions: () => void
  areAnytimeRates: boolean
  shouldShowAnytimeRate: boolean
  isLoading: boolean
  headlessMode: boolean
  durationLabel: string
  cheapestPropertyRates: [App.PropertyRate | App.Property.AnytimeRate | undefined, App.PropertyRate | undefined]
}

function PropertyStickyFooter(props: Props) {
  const {
    offer,
    onViewPackageOptions,
    areAnytimeRates,
    shouldShowAnytimeRate,
    isLoading,
    headlessMode,
    durationLabel,
    cheapestPropertyRates,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()

  const hideAnytimePrices = areAnytimeRates && !shouldShowAnytimeRate

  const onViewOptionsClick = useCallback(() => {
    if (hideAnytimePrices) {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else {
      dispatch({ type: OfferPageActions.closeFilter })
      onViewPackageOptions()
    }
  }, [hideAnytimePrices, dispatch, onViewPackageOptions])

  const [cheapestHotelOnlyRate] = cheapestPropertyRates

  const hasHotelPrice = !hideAnytimePrices && !!cheapestHotelOnlyRate

  const hotelPrice = cheapestHotelOnlyRate?.totals.displayPrice ?? 0
  const memberPrice = cheapestHotelOnlyRate?.luxPlusTotals?.displayPrice ?? 0

  const value = cheapestHotelOnlyRate?.luxPlusTotals?.valuedUpToPrice || cheapestHotelOnlyRate?.totals.valuedUpToPrice || 0
  const { shouldShowValue, shouldShowDiscountPercent, highestDiscountPercent } = getHotelOfferDisplayedValue(
    // TODO: Checking this LPC offer flag isn't appropriate if we're dealing with an LPP rate.
    !!offer.bundledWithFlightsOnly,
    memberPrice || hotelPrice,
    value,
    !!offer.shouldDisplayValue,
  )

  // Only show LuxPlus+ badging if the member price is _better_ than the non-member price.
  const showMemberPrice = !!memberPrice && memberPrice < hotelPrice

  const callToActionCopy = hideAnytimePrices
    ? intl.formatMessage({
        id: 'v06NzwRL/k',
        defaultMessage: 'Select dates',
        description: 'CTA text when dates need to be selected on property sticky footer',
      })
    : intl.formatMessage({
        id: 'cmfQfpT0XW',
        defaultMessage: 'View options',
        description: 'CTA text to view package options on property sticky footer',
      })

  return (
    <OfferPageStickyFooter
      visible
      callToActionCopy={callToActionCopy}
      onCTAClick={onViewOptionsClick}
      offer={offer}
    >
      {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
      <WalledContent enforced={offer.walledGarden}>
        {hasHotelPrice && hotelPrice > 0 && <Group direction="vertical">
          <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
          <LuxPlusPriceGuard
            showMemberPrice={showMemberPrice}
            fallbackPrice={<PriceRowPrice
              size="S"
              price={hotelPrice}
              saleUnit="total"
            />}
          >
            <PriceRowLuxPlusPrice
              size="M"
              memberPrice={memberPrice}
              saleUnit="total"
              logoPlacement="leading"
            />
          </LuxPlusPriceGuard>
          {shouldShowValue && shouldShowDiscountPercent && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={value}
            discountPercentage={highestDiscountPercent}
          />}
          <LoadingIndicator visible={isLoading} floating opaque />
        </Group>}
        {hideAnytimePrices && <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineInfoCircleIcon />}
        >
          {intl.formatMessage({
            id: 'MvIbRvib9n',
            defaultMessage: 'Select dates to get the best price for this offer',
            description: 'Info message when dates aren’t selected on property sticky footer',
          })}
        </BodyTextBlock>}
      </WalledContent>
    </OfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  // We only need to show anytime pricing for traffic coming from marketing, which will have
  // `utm_bp` set.
  shouldShowAnytimeRate: !!appState.utm.bp,
  isLoading: arePropertyRatesLoading(state, appState),
  headlessMode: appState.system.headlessMode,
  durationLabel: getPropertyFromDurationLabel(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  open: !!state.activeFilter,
}), PropertyStickyFooter, preventRenderWithOpenFilter)
